import { FormEvent, useState } from "react";

export interface ServiceGroupFormValues {
  name: string;
  code: string;
  description?: string;
}

interface Props {
  onClose: () => void;
  onSubmit: (values: ServiceGroupFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
}

/**
 * Sinh mã nhóm từ tên: bỏ dấu, viết hoa, ký tự khác chữ/số thành "_".
 * Mã chỉ là khoá đối chiếu nội bộ (vd DICH_VU_GIAT_SAY) — vẫn cho sửa tay nếu muốn.
 */
const codeFromName = (name: string): string =>
  (name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

/**
 * Tạo NHÓM dịch vụ (bảng `services`, vd "Dịch vụ giặt sấy"). Dịch vụ trong nhóm thêm riêng bằng
 * modal "Thêm dịch vụ". Nhóm dùng chung mọi chi nhánh (branch_id = 0) — trang cha gửi branch_id.
 */
export const ServiceGroupModal: React.FC<Props> = ({ onClose, onSubmit, isSubmitting = false }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  // Chưa sửa tay thì mã tự chạy theo tên
  const [codeTouched, setCodeTouched] = useState(false);
  const [description, setDescription] = useState("");

  const handleNameChange = (value: string) => {
    setName(value);
    if (!codeTouched) setCode(codeFromName(value));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) return;
    const ok = await onSubmit({ name: name.trim(), code: code.trim().toUpperCase(), description: description.trim() || undefined });
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-800">Tạo nhóm dịch vụ</h2>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full text-xl text-slate-400 hover:bg-slate-100">
            ×
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Tên nhóm <span className="text-rose-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="VD: Dịch vụ giặt sấy"
              required
              maxLength={255}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Mã nhóm <span className="text-rose-500">*</span>
            </label>
            <input
              value={code}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(e.target.value.toUpperCase());
              }}
              placeholder="Tự sinh từ tên"
              required
              maxLength={100}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm uppercase shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Mô tả</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mô tả ngắn (không bắt buộc)"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !name.trim() || !code.trim()}
            className="rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-green-700 disabled:opacity-50"
          >
            {isSubmitting ? "Đang lưu..." : "Tạo nhóm"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceGroupModal;
